import React, {useState, useDeferredValue} from "react";
import {Disclosure} from "@headlessui/react";
import {ChevronRightIcon, MagnifyingGlassIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {useBook, useSearchBooks} from "../hooks/useBook";

export default React.memo(function Sidebar({focusMode, activeBook, onSelectBook, isMobile, onClose}) {
    const [query, setQuery] = useState("");
    const deferredQuery = useDeferredValue(query);
    const {data: books} = useBook();
    const {data: searchResults, isFetching: searchLoading} = useSearchBooks(deferredQuery);
    const isSearching = !!deferredQuery;

    const clearSearch = () => setQuery("");

    return (<aside
        className={`h-full
        w-sidebar flex flex-col flex-shrink-0 relative overflow-hidden
        transition-opacity duration-500
        bg-gradient-to-b from-[#1e1508] via-[#241a09] to-[#1a1005]
        border-r border-[#3d2e14]
      `}
        style={{opacity: focusMode ? 0.08 : 1}}
    >
        {/* Gold shimmer line */}
        <div
            className="pointer-events-none absolute right-0 top-0 w-px h-full bg-gradient-to-b from-transparent via-gold to-transparent opacity-25 animate-shimmer"/>

        {/* Logo */}
        <div className="px-6 pt-7 pb-5 border-b border-gold/10 relative">
            {isMobile && (
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gold/40 hover:text-gold/80 p-1"
                    aria-label="关闭目录"
                >
                    <XMarkIcon className="w-5 h-5"/>
                </button>
            )}
            <h1 className="font-display text-[28px] text-gold-light tracking-[8px] text-center text-shadow-glow">
                典 藏
            </h1>
            <p className="text-[11px] text-ink-faint tracking-[4px] text-center mt-1.5">
                中国古典文献数字书馆
            </p>
        </div>

        {/* Search */}
        <div className="px-5 py-4 border-b border-white/5">
            <div
                className="flex items-center gap-2 bg-white/[0.04] border border-gold/20 rounded px-3 py-2 focus-within:border-gold/50 transition-colors">
                <MagnifyingGlassIcon className="w-4 h-4 text-ink-faint flex-shrink-0"/>
                <input
                    className="bg-transparent border-none outline-none text-paper-warm font-serif text-[13px] w-full placeholder:text-paper-warm/25"
                    placeholder="搜索书目…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                {isSearching && (
                    <button onClick={clearSearch} className="text-ink-faint hover:text-paper-warm">
                        <XMarkIcon className="w-4 h-4"/>
                    </button>
                )}
            </div>
        </div>

        {/* Book list */}
        <div className="flex-1 overflow-y-auto scrollbar-paper py-1">
            {isSearching ? (
                /* ── 搜索结果 ── */
                searchLoading ? (
                    <p className="text-center text-ink-faint/40 text-xs mt-8 tracking-[2px]">搜索中…</p>
                ) : searchResults?.data?.length > 0 ? (
                    searchResults.data.map((book) => (
                        <BookItem
                            key={book.id}
                            book={{Title: book.title, Dynasty: book.dynasty, ID: book.id}}
                            isActive={activeBook === book.title}
                            onClick={() => onSelectBook(book.title, book.id)}
                        />
                    ))
                ) : (
                    <p className="text-center text-ink-faint/30 text-xs mt-8 tracking-[2px]">未找到匹配书目</p>
                )
            ) : (
                /* ── 分类目录 ── */
                books?.data.map((group) => {
                    const filtered = group.Books;
                    if (filtered.length === 0) return null;
                    return (<Disclosure key={group.ID} defaultOpen>
                        {({open}) => (<div className="mb-1">
                            <Disclosure.Button
                                className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-white/[0.04] transition-colors group">
                        <span className="text-[10px] tracking-[4px] text-gold/60 flex-1 text-left">
                          {group.Name}
                        </span>
                                <div className="h-px flex-1 bg-gradient-to-r from-gold/25 to-transparent"/>
                                <ChevronRightIcon
                                    className={`w-3 h-3 text-gold/40 transition-transform duration-200 ${open ? "rotate-90" : ""}`}
                                />
                            </Disclosure.Button>

                            <Disclosure.Panel>
                                {filtered.map((book) => (<BookItem
                                    key={book.Title}
                                    book={book}
                                    isActive={activeBook === book.Title}
                                    onClick={() => onSelectBook(book.Title, book.ID)}
                                />))}
                            </Disclosure.Panel>
                        </div>)}
                    </Disclosure>);
                })
            )}
        </div>
    </aside>);
});

function BookItem({book, isActive, onClick}) {
    return (<button
        onClick={onClick}
        className={`
        w-full flex items-center gap-2 pl-6 pr-3 py-2.5 text-left relative
        rounded mx-1 font-serif text-[13px] tracking-wide
        transition-all duration-150 group
        ${isActive ? "bg-gold/15 text-gold-light" : "text-paper-warm/45 hover:bg-gold/8 hover:text-paper-warm/80"}
      `}
    >
        {/* Active indicator bar */}
        {isActive && (<span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-3/5 bg-gold rounded-r"/>)}
        <span className="flex-1">{book.Title}</span>
        <span className="text-[10px] text-ink-faint ml-auto">{book.Dynasty}</span>
    </button>);
}